/**
 * UserManagement Component
 * 
 * Admin-only component for managing application users.
 * Allows creating, viewing, updating roles, and deleting users.
 * 
 * @param onClose - Callback function to close the popup
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useFachbereiche } from '../contexts/FachbereicheContext';
import { useAuth } from '../contexts/AuthContext';
import { useBackendStatus } from '../contexts/BackendStatusContext';
import UserManagementEmailModal from './UserManagementEmailModal';
import './UserManagement.css';
import './AdminHeader.css';
import './Switch.css';

interface AppUser {
  id: number;
  username: string;
  role: 'Administrator' | 'Fachbereich Manager' | 'Fachbereich Administrator' | 'Beobachter';
  email?: string | null;
  isDeveloper?: boolean;
  created_at: string;
  last_login: string | null;
  fachbereiche?: string[];
}

interface OrphanedAssignment {
  userId: number;
  username: string;
  fachbereich: string;
}

interface UserManagementProps {
  onClose: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onClose }) => {
  const { fachbereiche, refreshFachbereiche } = useFachbereiche();
  const { token } = useAuth();
  const { pauseHealthCheck, resumeHealthCheck } = useBackendStatus();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'Administrator' | 'Fachbereich Manager' | 'Fachbereich Administrator' | 'Beobachter'>('Fachbereich Manager');
  const [newIsDeveloper, setNewIsDeveloper] = useState(false);
  const [editingEmail, setEditingEmail] = useState<{ [key: number]: string }>({});
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [editingFachbereiche, setEditingFachbereiche] = useState<number | null>(null);
  const [userFachbereiche, setUserFachbereiche] = useState<{ [key: number]: string[] }>({});
  const [updatingFachbereiche, setUpdatingFachbereiche] = useState<number | null>(null);
  const [openKebabMenuId, setOpenKebabMenuId] = useState<number | null>(null);
  const [orphanedAssignments, setOrphanedAssignments] = useState<OrphanedAssignment[]>([]);
  const [showOrphanedWarning, setShowOrphanedWarning] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [resettingPassword, setResettingPassword] = useState<number | null>(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState<number | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showBetreuerWarning, setShowBetreuerWarning] = useState(false);
  const [pendingFachbereicheUpdate, setPendingFachbereicheUpdate] = useState<{userId: number, fachbereiche: string[]} | null>(null);
  const [betreuerAssignmentsCount, setBetreuerAssignmentsCount] = useState<{fachbereich: string, count: number}[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [fachbereichFilter, setFachbereichFilter] = useState<string>('all');
  const [userFiltersExpanded, setUserFiltersExpanded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showCreateForm && showPasswordResetModal === null && !showOrphanedWarning && !showBetreuerWarning && !showEmailModal) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, showCreateForm, showPasswordResetModal, showOrphanedWarning, showBetreuerWarning, showEmailModal]);

  useEffect(() => {
    // Refresh Fachbereiche list when component opens to ensure deleted Fachbereiche don't appear
    refreshFachbereiche().then(() => {
      fetchUsers();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close kebab menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-management-kebab') && !target.closest('.user-management-kebab-menu')) {
        setOpenKebabMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/app-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setUsers(response.data.users);
        // Initialize userFachbereiche state
        const fachbereicheMap: { [key: number]: string[] } = {};
        response.data.users.forEach((user: AppUser) => {
          fachbereicheMap[user.id] = user.fachbereiche || [];
        });
        setUserFachbereiche(fachbereicheMap);
        
        // Check for orphaned assignments - always show warning if they exist
        if (response.data.orphanedAssignments && Array.isArray(response.data.orphanedAssignments) && response.data.orphanedAssignments.length > 0) {
          setOrphanedAssignments(response.data.orphanedAssignments);
          setShowOrphanedWarning(true);
        } else {
          setOrphanedAssignments([]);
          setShowOrphanedWarning(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupOrphanedAssignments = async () => {
    if (!window.confirm(
      `Möchten Sie wirklich ${orphanedAssignments.length} verwaiste Zuordnung(en) entfernen?\n\n` +
      `Dies kann nicht rückgängig gemacht werden. Bitte erstellen Sie vorher ein Backup, falls nötig.\n\n` +
      `Betroffene Zuordnungen:\n${orphanedAssignments.map(a => `- ${a.username}: ${a.fachbereich}`).join('\n')}`
    )) {
      return;
    }

    setCleaningUp(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/app-users/cleanup-orphaned-assignments`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setShowOrphanedWarning(false);
        setOrphanedAssignments([]);
        // Refresh Fachbereiche list after cleanup
        await refreshFachbereiche();
        await fetchUsers();
        alert(`Erfolgreich: ${response.data.deletedCount} verwaiste Zuordnung(en) entfernt.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Entfernen der verwaisten Zuordnungen');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate form
    if (!newUsername.trim()) {
      setError('Benutzername ist erforderlich');
      return;
    }
    if (!newPassword.trim() || newPassword.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    setError('');
    setCreating(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/app-users`, {
        username: newUsername.trim(),
        password: newPassword,
        role: newRole,
        email: newEmail?.trim() || null,
        isDeveloper: newIsDeveloper
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setNewUsername('');
        setNewPassword('');
        setNewEmail('');
        setNewRole('Fachbereich Manager');
        setNewIsDeveloper(false);
        setShowCreateForm(false);
        await fetchUsers();
      } else {
        setError(response.data.message || 'Fehler beim Erstellen des Benutzers');
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.message || err.message || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateRole = async (userId: number, newRole: 'Administrator' | 'Fachbereich Manager' | 'Fachbereich Administrator' | 'Beobachter') => {
    setUpdating(userId);
    setError('');

    try {
      const response = await axios.put(`${API_BASE_URL}/api/app-users/${userId}`, {
        role: newRole
      });

      if (response.data.success) {
        await fetchUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren der Rolle');
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    const userName = user ? user.username : 'diesen Benutzer';
    if (!window.confirm(`Möchten Sie den Benutzer "${userName}" wirklich löschen?`)) {
      return;
    }

    setDeleting(userId);
    setError('');

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/app-users/${userId}`);

      if (response.data.success) {
        await fetchUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen des Benutzers');
    } finally {
      setDeleting(null);
    }
  };

  const handleUpdateFachbereiche = async (userId: number) => {
    // Proceed with update - warnings are already shown when toggling checkboxes
    const fachbereiche = userFachbereiche[userId] || [];
    await performFachbereicheUpdate(userId, fachbereiche);
  };

  const performFachbereicheUpdate = async (userId: number, fachbereiche: string[]) => {
    setUpdatingFachbereiche(userId);
    setError('');

    try {
      const response = await axios.put(`${API_BASE_URL}/api/app-users/${userId}/fachbereiche`, {
        fachbereiche
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setEditingFachbereiche(null);
        // Refresh Fachbereiche list to ensure we have the latest data
        await refreshFachbereiche();
        await fetchUsers();
        
        // Show message if Betreuer assignments were removed
        if (response.data.removedBetreuerAssignments && response.data.removedBetreuerAssignments > 0) {
          setError(`Fachbereiche aktualisiert. ${response.data.removedBetreuerAssignments} Betreuer-Zuordnung(en) wurden entfernt.`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren der Fachbereiche');
    } finally {
      setUpdatingFachbereiche(null);
    }
  };

  const handleConfirmBetreuerRemoval = async () => {
    if (pendingFachbereicheUpdate && betreuerAssignmentsCount.length > 0) {
      setShowBetreuerWarning(false);
      
      // Remove Betreuer assignments immediately for cleanup
      try {
        for (const { fachbereich } of betreuerAssignmentsCount) {
          try {
            await axios.delete(
              `${API_BASE_URL}/api/app-users/${pendingFachbereicheUpdate.userId}/betreuer-assignments/${encodeURIComponent(fachbereich)}`,
              {
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
          } catch (err) {
            console.error(`Error removing Betreuer assignments for ${fachbereich}:`, err);
            // Continue with other Fachbereiche even if one fails
          }
        }
      } catch (err) {
        console.error('Error removing Betreuer assignments:', err);
        setError('Fehler beim Entfernen der Betreuer-Zuordnungen. Bitte versuchen Sie es erneut.');
      }
      
      // Update the local state to reflect the Fachbereich removal
      setUserFachbereiche({
        ...userFachbereiche,
        [pendingFachbereicheUpdate.userId]: pendingFachbereicheUpdate.fachbereiche
      });
      setPendingFachbereicheUpdate(null);
      setBetreuerAssignmentsCount([]);
    }
  };

  const handleCancelBetreuerRemoval = () => {
    setShowBetreuerWarning(false);
    setPendingFachbereicheUpdate(null);
    setBetreuerAssignmentsCount([]);
  };

  const handleFachbereichToggle = async (userId: number, fachbereich: string) => {
    const current = userFachbereiche[userId] || [];
    const isRemoving = current.includes(fachbereich);
    
    // If removing a Fachbereich, check for Betreuer assignments first
    if (isRemoving) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/app-users/${userId}/betreuer-assignments/${encodeURIComponent(fachbereich)}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data.success && response.data.count > 0) {
          // Show warning dialog
          setBetreuerAssignmentsCount([{ fachbereich, count: response.data.count }]);
          setPendingFachbereicheUpdate({ 
            userId, 
            fachbereiche: current.filter(fb => fb !== fachbereich) 
          });
          setShowBetreuerWarning(true);
          return; // Don't update state yet, wait for confirmation
        }
      } catch (err) {
        console.error('Error checking Betreuer assignments:', err);
        // Continue with toggle if check fails
      }
    }
    
    // No Betreuer assignments or adding Fachbereich - proceed with toggle
    const updated = isRemoving
      ? current.filter(fb => fb !== fachbereich)
      : [...current, fachbereich].sort();
    
    setUserFachbereiche({
      ...userFachbereiche,
      [userId]: updated
    });
  };

  const handleResetPassword = async (userId: number) => {
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (resetPasswordValue !== confirmPasswordValue) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }

    setResettingPassword(userId);
    setError('');

    try {
      const response = await axios.put(`${API_BASE_URL}/api/app-users/${userId}/password`, {
        newPassword: resetPasswordValue
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setShowPasswordResetModal(null);
        setResetPasswordValue('');
        setConfirmPasswordValue('');
        alert('Passwort erfolgreich zurückgesetzt');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Zurücksetzen des Passworts');
    } finally {
      setResettingPassword(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nie';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const getFilteredUsers = (): AppUser[] => {
    let list = users;
    if (roleFilter !== 'all') {
      list = list.filter(user => user.role === roleFilter);
    }
    if (fachbereichFilter !== 'all') {
      list = list.filter(user => {
        const fbs = userFachbereiche[user.id] ?? user.fachbereiche ?? [];
        return fbs.includes(fachbereichFilter);
      });
    }
    return list;
  };

  const getEmailRecipientLabel = (): string => {
    const roleNoun = roleFilter === 'all' ? null :
      roleFilter === 'Administrator' ? 'Administratoren' :
      roleFilter === 'Fachbereich Manager' ? 'Fachbereich Manager' :
      roleFilter === 'Fachbereich Administrator' ? 'Fachbereich Administratoren' :
      'Beobachter';
    const fb = fachbereichFilter === 'all' ? null : fachbereichFilter;
    if (!roleNoun && !fb) return 'sichtbare Benutzer';
    if (roleNoun && !fb) {
      return roleFilter === 'Administrator' ? 'sichtbare Administratoren' :
        roleFilter === 'Fachbereich Manager' ? 'sichtbare Fachbereich Manager' :
        roleFilter === 'Fachbereich Administrator' ? 'sichtbare Fachbereich Administratoren' :
        'sichtbare Beobachter';
    }
    if (!roleNoun && fb) return `sichtbare Benutzer im Fachbereich „${fb}“`;
    return `sichtbare ${roleNoun} im Fachbereich „${fb}“`;
  };

  const handleEmailVisibleUsers = () => {
    const filtered = getFilteredUsers();
    const emails = filtered
      .map((u) => (u.email && String(u.email).trim()) || null)
      .filter((e): e is string => !!e);
    if (emails.length === 0) {
      const roleLabel = roleFilter === 'all' ? 'Benutzer' :
        roleFilter === 'Administrator' ? 'Administratoren' :
        roleFilter === 'Fachbereich Manager' ? 'Fachbereich Manager' :
        roleFilter === 'Fachbereich Administrator' ? 'Fachbereich Administrator' : 'Beobachter';
      const fbSuffix = fachbereichFilter === 'all' ? '' : ` im Fachbereich „${fachbereichFilter}“`;
      alert(`Keine ${roleLabel}${fbSuffix} mit E-Mail-Adresse vorhanden.`);
      return;
    }
    setShowEmailModal(true);
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImporting(true);
    pauseHealthCheck(); // Pause health checks during import to prevent false "backend unavailable" warnings
    setImportMessage(null);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/api/app-users/import`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const summary = response.data.summary || {};
        void (summary.created ?? 0);
        void (summary.updated ?? 0);
        const errors = summary.errors || 0;
        
        let message = response.data.message || '';
        
        if (errors > 0 && summary.errorMessages && summary.errorMessages.length > 0) {
          const errorCount = summary.errorMessages.length;
          message += `. ${errorCount} Fehler${errorCount > 1 ? '' : ''}:`;
          if (errorCount <= 5) {
            message += ' ' + summary.errorMessages.join('; ');
          } else {
            message += ' ' + summary.errorMessages.slice(0, 5).join('; ') + ` und ${errorCount - 5} weitere`;
          }
        }

        setImportMessage({
          type: 'success',
          text: message
        });

        // Refresh user list
        await fetchUsers();

        // Clear message after 8 seconds
        setTimeout(() => {
          setImportMessage(null);
        }, 8000);
      } else {
        setImportMessage({
          type: 'error',
          text: response.data.message || 'Fehler beim Importieren der Benutzer'
        });
      }
    } catch (err: any) {
      let errorMessage = 'Fehler beim Importieren der Benutzer';
      
      if (err.response?.data) {
        errorMessage = err.response.data.message || errorMessage;
        if (err.response.data.summary?.errorMessages && err.response.data.summary.errorMessages.length > 0) {
          const errorList = err.response.data.summary.errorMessages.slice(0, 3).join('; ');
          errorMessage += `. Fehler: ${errorList}${err.response.data.summary.errorMessages.length > 3 ? '...' : ''}`;
        }
      } else if (err.message) {
        errorMessage += ` (${err.message})`;
      }

      setImportMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setImporting(false);
      resumeHealthCheck(); // Resume health checks after import completes
    }
  };

  return (
    <div className="user-management-overlay" onClick={onClose}>
      <div className="user-management-container" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <h2 className="admin-header-title">BENUTZERVERWALTUNG</h2>
          <button className="admin-header-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="user-management-error">{error}</div>}

        {showOrphanedWarning && orphanedAssignments.length > 0 && (
          <div className="user-management-warning" style={{
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '16px',
            color: '#856404'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.1em' }}>
              ⚠️ Verwaiste Zuordnungen gefunden
            </div>
            <div style={{ marginBottom: '12px' }}>
              Es wurden {orphanedAssignments.length} Zuordnung(en) gefunden, bei denen der Fachbereich nicht mehr existiert:
            </div>
            <ul style={{ marginLeft: '20px', marginBottom: '12px' }}>
              {orphanedAssignments.map((assignment, index) => (
                <li key={index}>
                  <strong>{assignment.username}</strong>: {assignment.fachbereich}
                </li>
              ))}
            </ul>
            <div style={{ marginBottom: '12px', fontSize: '0.9em', fontStyle: 'italic' }}>
              Diese Zuordnungen können entfernt werden. Bitte erstellen Sie vorher ein Backup, falls nötig.
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCleanupOrphanedAssignments}
                disabled={cleaningUp}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: cleaningUp ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {cleaningUp ? 'Entfernen...' : 'Verwaiste Zuordnungen entfernen'}
              </button>
              <button
                onClick={() => {
                  // Hide warning temporarily, but it will show again on next fetch if orphaned assignments still exist
                  setShowOrphanedWarning(false);
                }}
                disabled={cleaningUp}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: cleaningUp ? 'not-allowed' : 'pointer'
                }}
              >
                Später
              </button>
            </div>
          </div>
        )}

        <div className="user-management-actions">
          <button
            className="user-management-create-button"
            onClick={() => setShowCreateForm(!showCreateForm)}
            title={showCreateForm ? undefined : '+ Neuer Benutzer'}
          >
            {showCreateForm ? (
              'Abbrechen'
            ) : (
              <>
                <span className="user-management-create-label-full">+ Neuer Benutzer</span>
                <span className="user-management-create-label-short">+ Neu</span>
              </>
            )}
          </button>
          {!loading && users.length > 0 && (
            <button
              type="button"
              className="user-management-filters-toggle-mobile user-management-filters-toggle-in-actions"
              onClick={() => setUserFiltersExpanded(!userFiltersExpanded)}
              aria-label={userFiltersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}
              title={userFiltersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}
            >
              {userFiltersExpanded ? '▼' : '▲'}
            </button>
          )}
          <button
            className="user-management-import-button hide-import-on-mobile-phone"
            onClick={handleImportClick}
            disabled={importing}
            title="Benutzer aus Backup-Datei importieren (System-Benutzer Sheet)"
          >
            {importing ? '⏳' : '📤'} Import
          </button>
          <button
            className="user-management-email-all-button"
            onClick={handleEmailVisibleUsers}
            title="E-Mail an sichtbare Benutzer senden (Rolle- und Fachbereich-Filter berücksichtigt)"
          >
            <span className="user-management-email-label-full">✉️ E-Mail an sichtbare Benutzer</span>
            <span className="user-management-email-label-short">✉️ E-Mail</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileImport}
            style={{ display: 'none' }}
          />
        </div>

        {importMessage && (
          <div className={`user-management-import-message ${importMessage.type}`}>
            <div className="user-management-import-message-text">{importMessage.text}</div>
          </div>
        )}

        {showCreateForm && (
          <form onSubmit={handleCreateUser} className="user-management-create-form">
            {error && <div className="user-management-error" style={{ marginBottom: '16px' }}>{error}</div>}
            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#f0f7ff', 
              border: '1px solid #b3d9ff', 
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#004085'
            }}>
              <strong>Hinweis:</strong> Felder mit <span style={{ color: '#ed1c24', fontWeight: 'bold' }}>*</span> sind Pflichtfelder. Das Passwort muss mindestens 6 Zeichen lang sein.
            </div>
            <div className="user-management-form-group">
              <label>Benutzername <span style={{ color: '#ed1c24', fontWeight: 'bold' }}>*</span></label>
              <input
                type="text"
                id="new-username"
                name="new-username"
                value={newUsername}
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  e.target.setCustomValidity('');
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  (e.target as HTMLInputElement).setCustomValidity('Bitte füllen Sie dieses Feld aus');
                }}
                required
                disabled={creating}
              />
            </div>
            <div className="user-management-form-group">
              <label>Passwort <span style={{ color: '#ed1c24', fontWeight: 'bold' }}>*</span> <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 'normal' }}>(mindestens 6 Zeichen)</span></label>
              <input
                type="password"
                id="new-password"
                name="new-password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  e.target.setCustomValidity('');
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  const target = e.target as HTMLInputElement;
                  if (target.validity.valueMissing) {
                    target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
                  } else if (target.validity.tooShort) {
                    target.setCustomValidity('Bitte geben Sie mindestens 6 Zeichen ein');
                  }
                }}
                required
                disabled={creating}
                minLength={6}
              />
            </div>
            <div className="user-management-form-group">
              <label>E-Mail (optional)</label>
              <input
                type="email"
                id="new-email"
                name="new-email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  e.target.setCustomValidity('');
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  const target = e.target as HTMLInputElement;
                  if (target.validity.typeMismatch) {
                    target.setCustomValidity('Bitte geben Sie eine gültige E-Mail-Adresse ein');
                  }
                }}
                disabled={creating}
                placeholder="benutzer@example.com"
              />
            </div>
            <div className="user-management-form-group">
              <label>Rolle <span style={{ color: '#ed1c24', fontWeight: 'bold' }}>*</span></label>
              <select
                id="new-role"
                name="new-role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'Administrator' | 'Fachbereich Manager' | 'Fachbereich Administrator' | 'Beobachter')}
                disabled={creating}
                required
              >
                <option value="Fachbereich Manager">Fachbereich Manager</option>
                <option value="Fachbereich Administrator">Fachbereich Administrator</option>
                <option value="Beobachter">Beobachter</option>
                <option value="Administrator">Administrator</option>
              </select>
            </div>
            <div className="user-management-form-group">
              <label htmlFor="new-is-developer">
                <input
                  type="checkbox"
                  id="new-is-developer"
                  checked={newIsDeveloper}
                  onChange={(e) => setNewIsDeveloper(e.target.checked)}
                  disabled={creating}
                />
                {' '}Entwickler
              </label>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button 
                type="submit" 
                className="user-management-submit-button" 
                disabled={creating}
                style={{ flex: 1 }}
              >
                {creating ? 'Erstellt...' : 'Benutzer erstellen'}
              </button>
              <button 
                type="button" 
                className="user-management-submit-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCreateForm(false);
                  setNewUsername('');
                  setNewPassword('');
                  setNewEmail('');
                  setNewRole('Fachbereich Manager');
                  setNewIsDeveloper(false);
                  setError('');
                }}
                disabled={creating}
                style={{ 
                  flex: 1, 
                  background: 'transparent', 
                  border: '2px solid #666', 
                  color: '#666',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer',
                  borderRadius: 0,
                  padding: '10px 20px',
                  fontSize: '0.95rem'
                }}
              >
                Abbrechen
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="user-management-loading">Lädt...</div>
        ) : (
          <div className="user-management-list">
            {users.length > 0 && userFiltersExpanded && (
              <div className="user-management-filters-mobile-wrap">
                <div className="user-management-filters-mobile-panel-inner">
                  <div className="user-management-filter-mobile-field">
                    <label htmlFor="user-mgmt-role-filter-mobile">Rolle</label>
                    <select
                      id="user-mgmt-role-filter-mobile"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="user-management-role-filter"
                      title="Rolle filtern"
                    >
                      <option value="all">Alle</option>
                      <option value="Administrator">Administrator</option>
                      <option value="Fachbereich Manager">Fachbereich Manager</option>
                      <option value="Fachbereich Administrator">Fachbereich Administrator</option>
                      <option value="Beobachter">Beobachter</option>
                    </select>
                  </div>
                  <div className="user-management-filter-mobile-field">
                    <label htmlFor="user-mgmt-fb-filter-mobile">Fachbereich</label>
                    <select
                      id="user-mgmt-fb-filter-mobile"
                      value={fachbereichFilter}
                      onChange={(e) => setFachbereichFilter(e.target.value)}
                      className="user-management-role-filter user-management-fachbereich-filter"
                      title="Nach Fachbereich filtern"
                    >
                      <option value="all">Alle Fachbereiche</option>
                      {fachbereiche.map((fb) => (
                        <option key={fb} value={fb}>{fb}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
            {(() => {
              const filteredUsers = getFilteredUsers();
              const emptyFilterHint =
                users.length === 0
                  ? 'Keine Benutzer gefunden'
                  : (() => {
                      const parts: string[] = [];
                      if (roleFilter !== 'all') parts.push(`Rolle „${roleFilter}“`);
                      if (fachbereichFilter !== 'all') parts.push(`Fachbereich „${fachbereichFilter}“`);
                      return parts.length > 0
                        ? `Keine Benutzer mit ${parts.join(' und ')} gefunden`
                        : 'Keine Benutzer gefunden';
                    })();
              
              return filteredUsers.length === 0 ? (
                <div className="user-management-empty">
                  {emptyFilterHint}
                </div>
              ) : (
                <table className="user-management-table">
                  <thead>
                    <tr>
                      <th>Benutzername</th>
                      <th>E-Mail</th>
                      <th>
                        <div className="user-management-role-header">
                          <span>Rolle:</span>
                          <select
                            id="user-mgmt-role-filter-desktop"
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="user-management-role-filter"
                            title="Rolle filtern"
                          >
                            <option value="all">Alle</option>
                            <option value="Administrator">Administrator</option>
                            <option value="Fachbereich Manager">Fachbereich Manager</option>
                            <option value="Fachbereich Administrator">Fachbereich Administrator</option>
                            <option value="Beobachter">Beobachter</option>
                          </select>
                        </div>
                      </th>
                      <th>Entwickler</th>
                      <th>
                        <div className="user-management-role-header">
                          <span>Fachbereich:</span>
                          <select
                            id="user-mgmt-fb-filter-desktop"
                            value={fachbereichFilter}
                            onChange={(e) => setFachbereichFilter(e.target.value)}
                            className="user-management-role-filter user-management-fachbereich-filter"
                            title="Nach Fachbereich filtern"
                          >
                            <option value="all">Alle</option>
                            {fachbereiche.map((fb) => (
                              <option key={fb} value={fb}>{fb}</option>
                            ))}
                          </select>
                        </div>
                      </th>
                      <th>Letzte Anmeldung</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td data-label="Benutzername">{user.username}</td>
                      <td data-label="E-Mail">
                        {editingEmail[user.id] !== undefined ? (
                          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <input
                              type="email"
                              id={`user-email-${user.id}`}
                              name={`user-email-${user.id}`}
                              value={editingEmail[user.id]}
                              onChange={(e) => setEditingEmail(prev => ({ ...prev, [user.id]: e.target.value }))}
                              onBlur={async () => {
                                const emailValue = editingEmail[user.id];
                                if (emailValue !== (user.email || '')) {
                                  try {
                                    const response = await axios.put(`${API_BASE_URL}/api/app-users/${user.id}`, {
                                      role: user.role,
                                      email: emailValue || null
                                    });
                                    if (response.data.success) {
                                      await fetchUsers();
                                      setEditingEmail(prev => {
                                        const updated = { ...prev };
                                        delete updated[user.id];
                                        return updated;
                                      });
                                    }
                                  } catch (err: any) {
                                    setError(err.response?.data?.message || 'Fehler beim Aktualisieren der E-Mail');
                                    setEditingEmail(prev => {
                                      const updated = { ...prev };
                                      delete updated[user.id];
                                      return updated;
                                    });
                                  }
                                } else {
                                  setEditingEmail(prev => {
                                    const updated = { ...prev };
                                    delete updated[user.id];
                                    return updated;
                                  });
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingEmail(prev => {
                                    const updated = { ...prev };
                                    delete updated[user.id];
                                    return updated;
                                  });
                                }
                              }}
                              style={{ width: '100%', maxWidth: '100%', padding: '8px 12px', fontSize: '1rem', minHeight: '44px', boxSizing: 'border-box' }}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div 
                            style={{ cursor: 'pointer', color: user.email ? '#ed1c24' : '#666' }}
                            onClick={() => setEditingEmail(prev => ({ ...prev, [user.id]: user.email || '' }))}
                            title="Klicken zum Bearbeiten"
                          >
                            {user.email ? (
                              <a href={`mailto:${user.email}`} onClick={(e) => e.stopPropagation()}>
                                {user.email}
                              </a>
                            ) : (
                              <span style={{ fontStyle: 'italic', color: '#999' }}>Keine E-Mail</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td data-label="Rolle">
                        <select
                          id={`user-role-${user.id}`}
                          name={`user-role-${user.id}`}
                          value={user.role}
                          onChange={(e) => handleUpdateRole(user.id, e.target.value as 'Administrator' | 'Fachbereich Manager' | 'Fachbereich Administrator' | 'Beobachter')}
                          disabled={updating === user.id}
                          className="user-management-role-select"
                        >
                          <option value="Fachbereich Manager">Fachbereich Manager</option>
                          <option value="Fachbereich Administrator">Fachbereich Administrator</option>
                          <option value="Beobachter">Beobachter</option>
                          <option value="Administrator">Administrator</option>
                        </select>
                      </td>
                      <td data-label="Entwickler">
                        <label className={`switch-container ${updating === user.id ? 'disabled' : ''}`} style={{ opacity: updating === user.id ? 0.6 : 1 }}>
                          <input
                            type="checkbox"
                            id={`user-developer-${user.id}`}
                            className="switch-input"
                            checked={user.isDeveloper || false}
                            onChange={async (e) => {
                              const newIsDeveloper = e.target.checked;
                              try {
                                setUpdating(user.id);
                                const response = await axios.put(`${API_BASE_URL}/api/app-users/${user.id}`, {
                                  role: user.role,
                                  email: user.email || null,
                                  isDeveloper: newIsDeveloper
                                });
                                if (response.data.success) {
                                  await fetchUsers();
                                }
                              } catch (err: any) {
                                setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Entwickler-Status');
                              } finally {
                                setUpdating(null);
                              }
                            }}
                            disabled={updating === user.id}
                            title={user.isDeveloper ? "Entwickler aktiviert" : "Entwickler deaktiviert"}
                          />
                          <span className="switch-track switch-track-red">
                            <span className="switch-thumb" />
                          </span>
                        </label>
                      </td>
                      <td data-label="Fachbereiche">
                        {editingFachbereiche === user.id ? (
                          <div className="user-management-fachbereiche-edit">
                            <div className="user-management-fachbereiche-checkboxes">
                  {fachbereiche.map((fb) => (
                                <label key={fb} className="user-management-fachbereich-checkbox">
                                  <input
                                    type="checkbox"
                                    id={`user-fachbereich-${user.id}-${fb}`}
                                    name={`user-fachbereich-${user.id}-${fb}`}
                                    checked={(userFachbereiche[user.id] || []).includes(fb)}
                                    onChange={() => handleFachbereichToggle(user.id, fb)}
                                    disabled={updatingFachbereiche === user.id}
                                  />
                                  <span>{fb}</span>
                                </label>
                              ))}
                            </div>
                            <div className="user-management-fachbereiche-actions">
                              <button
                                className="user-management-fachbereiche-save"
                                onClick={() => handleUpdateFachbereiche(user.id)}
                                disabled={updatingFachbereiche === user.id}
                              >
                                {updatingFachbereiche === user.id ? 'Speichert...' : 'Speichern'}
                              </button>
                              <button
                                className="user-management-fachbereiche-cancel"
                                onClick={() => {
                                  setEditingFachbereiche(null);
                                  // Reset to original values, filtering out non-existent Fachbereiche
                                  const validFachbereiche = (user.fachbereiche || []).filter(fb => fachbereiche.includes(fb));
                                  setUserFachbereiche({
                                    ...userFachbereiche,
                                    [user.id]: validFachbereiche
                                  });
                                }}
                                disabled={updatingFachbereiche === user.id}
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="user-management-fachbereiche-display">
                            {(() => {
                              // Filter out any Fachbereiche that don't exist in the current year's Fachbereiche list
                              const validFachbereiche = (user.fachbereiche || []).filter(fb => fachbereiche.includes(fb));
                              return validFachbereiche.length > 0 ? (
                                <div className="user-management-fachbereiche-list">
                                  {validFachbereiche.join(', ')}
                                </div>
                              ) : (
                                <span className="user-management-fachbereiche-none">Keine</span>
                              );
                            })()}
                          </div>
                        )}
                      </td>
                      <td data-label="Letzte Anmeldung">{formatDate(user.last_login)}</td>
                      <td className="user-management-actions-cell">
                        <div style={{ position: 'relative' }}>
                          <button
                            className="user-management-kebab"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenKebabMenuId(openKebabMenuId === user.id ? null : user.id);
                            }}
                            title="Optionen"
                          >
                            ⋮
                          </button>
                          {openKebabMenuId === user.id && (
                            <div className="user-management-kebab-menu">
                              <button
                                className="user-management-kebab-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenKebabMenuId(null);
                                  setEditingEmail(prev => ({ ...prev, [user.id]: user.email || '' }));
                                }}
                                disabled={editingEmail[user.id] !== undefined}
                              >
                                E-Mail bearbeiten
                              </button>
                              <button
                                className="user-management-kebab-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenKebabMenuId(null);
                                  setEditingFachbereiche(user.id);
                                  const validFachbereiche = (user.fachbereiche || []).filter(fb => fachbereiche.includes(fb));
                                  setUserFachbereiche({
                                    ...userFachbereiche,
                                    [user.id]: validFachbereiche
                                  });
                                }}
                                disabled={updatingFachbereiche === user.id}
                              >
                                Bereiche zuordnen
                              </button>
                              <div className="user-management-kebab-divider"></div>
                              <button
                                className="user-management-kebab-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenKebabMenuId(null);
                                  setShowPasswordResetModal(user.id);
                                  setResetPasswordValue('');
                                  setConfirmPasswordValue('');
                                }}
                                disabled={resettingPassword === user.id}
                              >
                                Passwort zurücksetzen
                              </button>
                              <button
                                className="user-management-kebab-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenKebabMenuId(null);
                                  handleDeleteUser(user.id);
                                }}
                                disabled={deleting === user.id}
                              >
                                {deleting === user.id ? 'Löscht...' : 'Löschen'}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              );
            })()}
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordResetModal !== null && (
          <div className="user-management-modal-overlay" onClick={() => {
            if (resettingPassword === null) {
              setShowPasswordResetModal(null);
              setResetPasswordValue('');
              setConfirmPasswordValue('');
              setError('');
            }
          }}>
            <div className="user-management-modal" onClick={(e) => e.stopPropagation()}>
              <div className="user-management-modal-header">
                <h3>Passwort zurücksetzen</h3>
                <button 
                  className="user-management-modal-close"
                  onClick={() => {
                    if (resettingPassword === null) {
                      setShowPasswordResetModal(null);
                      setResetPasswordValue('');
                      setConfirmPasswordValue('');
                      setError('');
                    }
                  }}
                  disabled={resettingPassword !== null}
                >
                  ×
                </button>
              </div>
              <div className="user-management-modal-body">
                <p style={{ marginBottom: '16px' }}>
                  Neues Passwort für Benutzer <strong>{users.find(u => u.id === showPasswordResetModal)?.username}</strong> festlegen:
                </p>
                {error && <div className="user-management-error" style={{ marginBottom: '16px' }}>{error}</div>}
                <div className="user-management-form-group">
                  <label>Neues Passwort</label>
                  <input
                    type="password"
                    value={resetPasswordValue}
                    onChange={(e) => {
                      setResetPasswordValue(e.target.value);
                      setError('');
                    }}
                    disabled={resettingPassword !== null}
                    minLength={6}
                    placeholder="Mindestens 6 Zeichen"
                    autoFocus
                  />
                </div>
                <div className="user-management-form-group">
                  <label>Passwort bestätigen</label>
                  <input
                    type="password"
                    value={confirmPasswordValue}
                    onChange={(e) => {
                      setConfirmPasswordValue(e.target.value);
                      setError('');
                    }}
                    disabled={resettingPassword !== null}
                    minLength={6}
                    placeholder="Passwort wiederholen"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && resetPasswordValue && confirmPasswordValue && resetPasswordValue === confirmPasswordValue) {
                        handleResetPassword(showPasswordResetModal);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="user-management-modal-footer">
                <button
                  className="user-management-modal-button user-management-modal-button-primary"
                  onClick={() => handleResetPassword(showPasswordResetModal)}
                  disabled={resettingPassword !== null || !resetPasswordValue || !confirmPasswordValue || resetPasswordValue.length < 6 || resetPasswordValue !== confirmPasswordValue}
                >
                  {resettingPassword !== null ? 'Zurücksetzen...' : 'Passwort zurücksetzen'}
                </button>
                <button
                  className="user-management-modal-button"
                  onClick={() => {
                    if (resettingPassword === null) {
                      setShowPasswordResetModal(null);
                      setResetPasswordValue('');
                      setConfirmPasswordValue('');
                      setError('');
                    }
                  }}
                  disabled={resettingPassword !== null}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {showEmailModal && (
          <UserManagementEmailModal
            bccEmails={getFilteredUsers()
              .map((u) => (u.email && String(u.email).trim()) || '')
              .filter(Boolean)}
            recipientLabel={getEmailRecipientLabel()}
            onClose={() => setShowEmailModal(false)}
          />
        )}

        {showBetreuerWarning && pendingFachbereicheUpdate && betreuerAssignmentsCount.length > 0 && (
          <div className="user-management-overlay" onClick={handleCancelBetreuerRemoval}>
            <div className="user-management-modal" onClick={(e) => e.stopPropagation()}>
              <div className="user-management-modal-header">
                <h3>Betreuer-Zuordnungen werden entfernt</h3>
                <button 
                  className="user-management-modal-close"
                  onClick={handleCancelBetreuerRemoval}
                  disabled={updatingFachbereiche === pendingFachbereicheUpdate.userId}
                >
                  ×
                </button>
              </div>
              <div className="user-management-modal-body">
                <p style={{ marginBottom: '16px' }}>
                  Der Benutzer <strong>{users.find(u => u.id === pendingFachbereicheUpdate.userId)?.username}</strong> hat Betreuer-Zuordnungen in folgenden Fachbereichen:
                </p>
                <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
                  {betreuerAssignmentsCount.map(({ fachbereich, count }) => (
                    <li key={fachbereich} style={{ marginBottom: '8px' }}>
                      <strong>{fachbereich}:</strong> {count} Zuordnung{count !== 1 ? 'en' : ''}
                    </li>
                  ))}
                </ul>
                <p style={{ marginBottom: '16px', fontWeight: 'bold', color: '#d32f2f' }}>
                  Gesamt: {betreuerAssignmentsCount.reduce((sum, item) => sum + item.count, 0)} Betreuer-Zuordnung{betreuerAssignmentsCount.reduce((sum, item) => sum + item.count, 0) !== 1 ? 'en' : ''} werden entfernt.
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Möchten Sie fortfahren? Die Betreuer-Zuordnungen werden automatisch entfernt, wenn der Fachbereich entfernt wird.
                </p>
              </div>
              <div className="user-management-modal-footer">
                <button
                  className="user-management-modal-button user-management-modal-button-primary"
                  onClick={handleConfirmBetreuerRemoval}
                  disabled={updatingFachbereiche === pendingFachbereicheUpdate.userId}
                >
                  {updatingFachbereiche === pendingFachbereicheUpdate.userId ? 'Wird entfernt...' : 'Fortfahren'}
                </button>
                <button
                  className="user-management-modal-button"
                  onClick={handleCancelBetreuerRemoval}
                  disabled={updatingFachbereiche === pendingFachbereicheUpdate.userId}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;

